import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, ArrowRight, Facebook, AtSign } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

interface ShowcaseItem {
  id?: string;
  platform: 'ig' | 'li' | 'x' | 'fb' | 'tr';
  imageUrl: string;
  imageUrls?: string[];
  title: string;
  brandName: string;
}

export function HomeShowcase() {
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const { isBrazil } = useTranslation();

  useEffect(() => {
    const fetchShowcase = async () => {
      try {
        const q = query(collection(db, 'showcase_items'), orderBy('createdAt', 'desc'), limit(3));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShowcaseItem));
        setItems(data);
      } catch (err) {
        console.error("Error fetching showcase items:", err);
      }
    };
    fetchShowcase();
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-[#030303]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="space-y-4">
            <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
              Showcase
            </p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-white px-[0px]">
              {isBrazil ? "Conteúdo em Destaque" : "Featured Content"}
            </h2>
          </div>
          <Link 
            to="/showcase" 
            className="group flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-widest text-sm transition-colors"
          >
            {isBrazil ? "Ver Showcase" : "View Showcase"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {items.map(item => (
            <motion.div 
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group border border-white/5 rounded-[2rem] hover:bg-white/5 transition-all p-4"
            >
              <Link to={`/showcase/${item.id}`} className="block">
                <div className="relative aspect-square overflow-hidden rounded-[1.5rem] border border-white/5 mb-6">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    referrerPolicy="no-referrer" 
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-2xl backdrop-blur-md ${
                        item.platform === 'ig' ? 'bg-gradient-to-tr from-yellow-400/80 via-red-500/80 to-purple-600/80 border border-white/20' :
                        item.platform === 'li' ? 'bg-blue-600/80 border border-white/20' :
                        item.platform === 'fb' ? 'bg-[#1877F2]/80 border border-white/20' :
                        item.platform === 'tr' ? 'bg-black/80 border border-white/40' :
                        'bg-black/80 border border-white/20'
                    }`}>
                      {item.platform === 'ig' ? 'Instagram' : item.platform === 'li' ? 'LinkedIn' : item.platform === 'fb' ? 'Facebook' : item.platform === 'tr' ? 'Threads' : 'X'}
                    </span>
                  </div>
                  {item.imageUrls && item.imageUrls.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/80 px-3 py-1 rounded-full text-xs font-bold text-white border border-white/20">
                      +{item.imageUrls.length - 1} images
                    </div>
                  )}
                </div>
                <div className="space-y-4 px-2 pb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-3 py-1 rounded-lg border border-indigo-500/10">
                      {item.brandName}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-white leading-tight tracking-tight line-clamp-2">
                    {item.title}
                  </h3>
                  <span className="text-indigo-400 text-xs font-bold mt-2 inline-block group-hover:translate-x-1 transition-transform">Read study &rarr;</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
